import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { BusinessUnit } from 'app/shared/models/business-unit.model';
import { NotificationService } from 'app/shared/services/notification.service';
import { BusinessUnitsService } from 'app/shared/services/business-units.service';
import { take } from 'rxjs/operators';
import {
  ClientService,
  JurisdictionService,
  UserService
} from 'app/shared/services';
import { Enum } from 'app/shared/models/enum.model';
import { Constants } from 'app/shared/config/constants';
import { SafeHtml } from "@angular/platform-browser";

@Component ( {
  selector: 'reporter-business-unit-advanced-search',
  templateUrl: './business-unit-advanced-search.component.html',
  styleUrls: [ './business-unit-advanced-search.component.scss' ],
  standalone: false
} )
export class BusinessUnitAdvancedSearchComponent implements OnInit, OnChanges {

  @Input () reportingLocationsOnly = false;
  @Input () activeDate: string;
  @Input () parentLocationIds: string;
  @Output () selectBU = new EventEmitter<BusinessUnit> ();

  advancedSearchOpen = true;
  searchModel: any = {};
  searchResults: BusinessUnit[];
  jurisdictions: Enum[];
  pageNumber = 1;
  inFocus: BusinessUnit;
  totalRecords = 0;
  totalPages = 0;
  buReferenceNumberPrompt: string;
  systemClientSelected: boolean;

  constructor (
    private notificationService: NotificationService,
    private businessUnitsService: BusinessUnitsService,
    private jurisdictionService: JurisdictionService,
    private clientService: ClientService,
    private userService: UserService
  ) {
    this.systemClientSelected = this.userService.currentClient?.clientNumber?.toUpperCase () === 'SYSTEM';
  }

  ngOnInit () {
    this.jurisdictionService.allByCountry ()
      .pipe ( take ( 1 ) )
      .subscribe (
        jurisdictions => this.jurisdictions = jurisdictions
      );
    this.applyDefaultSearchCriteria ();
    this.buReferenceNumberPrompt =
      this.clientService.buTextSettingsByKey ( 'BU_REFERENCE_NUMBER_PROMPT' ) || Constants.buReferenceNumberPrompt;
  }

  ngOnChanges ( changes: SimpleChanges ) {
    if ( changes.activeDate || changes.parentLocationIds || changes.reportingLocationsOnly ) {
      this.applyDefaultSearchCriteria ();
    }
  }

  submit () {
    this.notificationService.clearAll ();
    this.searchResults = [];
    this.pageNumber = 1;

    this.businessUnitsService.businessUnitsSearch ( this.searchModel )
      .pipe ( take ( 1 ) )
      .subscribe ( results => {
        this.searchResults = results.records;
        this.totalRecords = results.totalRecords;
        this.totalPages = results.totalPages;
        if ( !this.searchResults?.length ) {
          this.advancedSearchOpen = true;
          this.notificationService.addNotification ( 'warning', 'No records found.' );
        } else {
          this.advancedSearchOpen = false;
        }
      } );
  }

  loadNextPage () {
    this.pageNumber++;
    this.businessUnitsService.businessUnitsSearch ( this.searchModel, this.pageNumber )
      .pipe ( take ( 1 ) )
      .subscribe ( results => {
        if ( results?.records?.length ) {
          this.searchResults = [ ...this.searchResults, ...results.records ];
        }
      } );
  }

  emitSelected ( bu: BusinessUnit ) {
    this.selectBU.emit ( bu );
    this.inFocus = undefined;
  }

  resetForm () {
    this.searchModel = {};
    this.searchResults = undefined;
    this.applyDefaultSearchCriteria ();
  }

  private applyDefaultSearchCriteria () {
    if ( this.reportingLocationsOnly ) {
      this.searchModel.reportingLocationsOnly = true;
    } else {
      this.searchModel.reportingLocationsOnly = false;
    }
    if ( this.activeDate ) {
      this.searchModel.activeDate = this.activeDate;
    } else {
      delete this.searchModel.activeDate;
    }
    if ( this.parentLocationIds ) {
      this.searchModel.parentLocationIds = this.parentLocationIds;
    } else {
      delete this.searchModel.parentLocationIds;
    }
  }

  highlightName ( text: string ): SafeHtml {
    if ( !this.searchModel[ 'name' ] ) {
      return text as unknown as SafeHtml;
    }
    if ( this.searchModel[ 'name' ] ) {
      const regex = new RegExp ( `(${ this.searchModel['name'] })`, 'gi' );
      const highlighted = text.replace ( regex, '<span class="highlight">$1</span>' );
      return highlighted as unknown as SafeHtml;
    }
    return text as unknown as SafeHtml;
  }
}
